import { useRef, useState } from 'react';
import { getFileNameFromPath } from '@goldcast/player-config/src/utils/fileUtils';
import Loader from '@/components/atoms/Loader';
import IconButton from '@/components/atoms/Button/IconButton';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';
import useDialog from '@/components/organisms/useDialog';
import { classnames } from '@/libs/utils';
import { BrandKitMediaCardProps } from '@/Pages/BrandKit/types';

export default function BrandKitMediaCard({
  url,
  label,
  isSelected,
  onClick,
  type,
  actions,
  onDelete,
  onDurationAvailable,
  className = ''
}: BrandKitMediaCardProps): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const { isOpen: isDeleteOpen, openDialog: openDelete, closeDialog: closeDelete } = useDialog();

  function playOnHover() {
    if (videoRef.current && videoRef.current.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      videoRef.current.play();
    }
  }

  function pauseOnLeave() {
    if (videoRef.current && videoRef.current.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      videoRef.current.pause();
    }
  }

  function handleVideoReady() {
    setIsVideoLoaded(true);
    if (videoRef.current) {
      onDurationAvailable?.(videoRef.current.duration);
    }
  }

  const deleteLabel = type === 'image' ? 'Delete Image' : 'Delete Video';

  return (
    <>
      <button
        type="button"
        data-testid={`uploaded-background-${type}`}
        onClick={onClick}
        className={classnames(
          'flex shrink-0 flex-col overflow-hidden rounded-lg border-2 text-left transition-all',
          isSelected
            ? 'border-deep-orange-500 bg-deep-orange-50 hover:border-deep-orange-500'
            : 'border-slate-200 hover:border-deep-orange-200 hover:bg-deep-orange-50',
          className
        )}
      >
        {type === 'image' && (
          <div className="h-[129px] w-full overflow-hidden bg-slate-100">
            {url && <img src={url} alt={label ?? 'Brand kit media'} className="size-full object-cover object-center" />}
          </div>
        )}

        {type === 'video' && (
          <div
            className="group relative h-[129px] w-full overflow-hidden bg-slate-100"
            onMouseEnter={playOnHover}
            onMouseLeave={pauseOnLeave}
          >
            <video
              ref={videoRef}
              muted
              className="absolute inset-0 size-full object-cover"
              onCanPlay={handleVideoReady}
            >
              <source src={url} type="video/mp4" />
            </video>

            {!isVideoLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader />
              </div>
            )}

            {actions && (
              <div className="absolute inset-0 flex flex-col justify-end opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-black/0" />
                <div className="relative z-10 flex w-full justify-between p-2">{actions}</div>
              </div>
            )}
          </div>
        )}

        <div className="flex w-full items-center justify-between gap-1 px-2 py-1.5">
          <p className="truncate text-sm text-slate-950">{getFileNameFromPath(label ?? url)}</p>
          {onDelete && (
            <IconButton
              icon="IconTrash"
              size="small"
              variation="text"
              trackingId={`uploaded-background-${type}`}
              onClick={e => {
                e.stopPropagation();
                openDelete();
              }}
            />
          )}
        </div>
      </button>

      {onDelete && (
        <ConfirmationDialog
          isOpen={isDeleteOpen}
          onClose={closeDelete}
          onConfirm={onDelete}
          title={deleteLabel}
          content={`Are you sure you want to remove this ${type}?`}
          confirmLabel="Yes"
        />
      )}
    </>
  );
}
